import { useEffect } from "react";

import { useTourContext } from "../context/tour-context";
import { resolveTourSelector } from "../utils/positioning";
import { isTourCompleted } from "../utils/tour-storage";

// AIDEV-NOTE: Convenient hook for accessing tour functionality
// Re-exports context hook with additional utilities

export { useTourContext as useTour };

/**
 * SSR-safe helper to check if current device is mobile
 * Based on Tailwind breakpoints: md = 768px
 */
function isMobileDevice(): boolean {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768;
}

/**
 * Hook to automatically trigger a tour on mount if conditions are met
 * @param tourId - ID of the tour to trigger
 * @param shouldShow - Optional condition function to determine if tour should show
 */
export function useAutoTour(tourId: string, shouldShow?: () => boolean): void {
    const { openWelcomeModal, getTourById } = useTourContext();

    useEffect(() => {
        // AIDEV-NOTE: Skip tours in automated tests unless explicitly enabled
        // This prevents tour modals from interfering with test assertions
        // To enable tours in specific tests, set window.env.DISABLE_TOURS_IN_TESTS = 'false'
        if (
            typeof window !== "undefined" &&
            window.env?.DISABLE_TOURS_IN_TESTS === "true"
        ) {
            return;
        }

        // AIDEV-NOTE: Tours are desktop-only; don't show on mobile (< md breakpoint)
        // Checked inside effect for SSR safety
        if (isMobileDevice()) {
            return;
        }

        // Don't show if already completed
        if (isTourCompleted(tourId)) {
            return;
        }

        // Check custom condition if provided
        if (shouldShow && !shouldShow()) {
            return;
        }

        // Verify tour exists
        const tour = getTourById(tourId);
        if (!tour) {
            return;
        }

        // AIDEV-NOTE: Open welcome modal after a short delay (let page render first).
        // After delay, verify the tour's target elements are actually visible in the DOM.
        // This prevents showing the welcome modal when target buttons are conditionally
        // hidden (e.g., session not COMPLETED, container query hiding, etc.)
        const timer = setTimeout(() => {
            const tourConfig = getTourById(tourId);
            if (tourConfig?.steps?.length) {
                const firstStep = tourConfig.steps[0];
                const selector = resolveTourSelector(firstStep.targetSelector);
                const target = document.querySelector(selector);
                if (!target || (target as HTMLElement).offsetParent === null) {
                    return;
                }
            }
            openWelcomeModal(tourId);
        }, 500);

        return () => clearTimeout(timer);
    }, [tourId, shouldShow, openWelcomeModal, getTourById]);
}
